import React, {
  cloneElement,
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { oneOf } from 'prop-types';
import { Menu, MenuItem } from '@material-ui/core';
import classNames from 'classnames';
import Clickable from 'common/Clickable';
import useStyles from './useStyles';

const ThemedMenuItem = forwardRef(
  (
    {
      type = 'default',
      value = '',
      text = '',
      leftIcon,
      rightIcon,
      children,
      selected = false,
      onClick,
      rootClassName = '',
      selectedClassName = '',
      MenuItemProps,
      parentOpen,
      NestedMenuProps,
      title = '',
      disabled = false,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const classes = useStyles();
    const rootClasses = classNames(classes.root, rootClassName);
    const selectedClasses = classNames(classes.selected, selectedClassName);

    const menuItemRef = useRef(null);
    useImperativeHandle(ref, () => menuItemRef.current);

    const handleClick = (e) => {
      if (type === 'default') {
        onClick({ event: e, option: text, value });
      } else {
        onClick(e);
      }
    };

    const handleMouseEnter = useCallback(() => {
      if (type === 'nested') setIsOpen(true);
    }, [type]);

    const handleMouseLeave = useCallback(() => {
      if (type === 'nested') setIsOpen(false);
    }, [type]);

    const menuOpen = isOpen && parentOpen;

    let view;

    switch (type) {
      case 'default':
        view = (
          <MenuItem
            disableGutters
            onClick={handleClick}
            selected={selected}
            title={title}
            disabled={disabled}
            classes={{
              root: rootClasses,
              selected: selectedClasses,
            }}
            {...MenuItemProps}
          >
            {leftIcon && cloneElement(leftIcon, { className: classes.leftIcon })}
            {text || children} {/* be careful with children */}
            {rightIcon && cloneElement(rightIcon, { className: classes.rightIcon })}
          </MenuItem>
        );
        break;
      case 'nested':
        view = (
          <Clickable
            type="div"
            onMouseEnter={handleMouseEnter}
            onClick={handleClick}
            onMouseLeave={handleMouseLeave}
          >
            <MenuItem
              ref={menuItemRef}
              disableGutters
              disabled={disabled}
              title={title}
              classes={{
                root: rootClasses,
                selected: selectedClasses,
              }}
              {...MenuItemProps}
            >
              {leftIcon && cloneElement(leftIcon, { className: classes.leftIcon })}
              {text}
              {rightIcon && cloneElement(rightIcon, { className: classes.rightIcon })}
            </MenuItem>
            <Menu
              anchorEl={menuItemRef.current}
              style={{ pointerEvents: 'none' }}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={menuOpen}
              disableAutoFocus
              disableEnforceFocus
              onClose={() => setIsOpen(false)}
              {...NestedMenuProps}
            >
              <div style={{ pointerEvents: 'auto' }}>{children}</div>
            </Menu>
          </Clickable>
        );
        break;
      default:
        view = (
          <MenuItem
            disableGutters
            onClick={handleClick}
            selected={selected}
            disabled={disabled}
            title={title}
            classes={{
              root: rootClasses,
              selected: selectedClasses,
            }}
            {...MenuItemProps}
          >
            {leftIcon && cloneElement(leftIcon, { className: classes.leftIcon })}
            {text || children} {/* be careful with children */}
            {rightIcon && cloneElement(rightIcon, { className: classes.rightIcon })}
          </MenuItem>
        );
    }
    return view;
  }
);

ThemedMenuItem.propTypes = {
  type: oneOf(['default', 'nested']),
};

export default memo(ThemedMenuItem);
